import React, { useState } from 'react'
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js'
import { toast } from 'react-toastify'

interface PaymentFormProps {
  clientSecret: string
  onSuccess: () => void
  onError: (error: string) => void
}

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
  },
}

export const PaymentForm: React.FC<PaymentFormProps> = ({
  clientSecret,
  onSuccess,
  onError,
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setIsProcessing(true)

    const cardNumber = elements.getElement(CardNumberElement)

    if (!cardNumber) {
      setIsProcessing(false)
      onError('Card element not found')
      return
    }

    try {
      const { error } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardNumber,
        },
      })

      if (error) {
        console.error('Payment failed:', error)
        onError(error.message || 'Payment failed')
        toast.error(error.message || 'Payment failed')
      } else {
        onSuccess()
        toast.success('Payment successful!')
      }
    } catch (err) {
      console.error('Payment error:', err)
      onError('Payment processing failed')
      toast.error('Payment processing failed')
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div
      style={{
        maxWidth: '400px',
        margin: '0 auto',
        padding: '20px',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      }}
    >
      <h2 style={{ textAlign: 'center', marginBottom: '20px', color: '#333' }}>
        Complete Your Payment
      </h2>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '20px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '5px',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#333',
            }}
          >
            Card Number
          </label>
          <div
            style={{
              padding: '12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: '#fff',
            }}
          >
            <CardNumberElement options={cardElementOptions} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <div style={{ flex: 1 }}>
            <label
              style={{
                display: 'block',
                marginBottom: '5px',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#333',
              }}
            >
              Expiry Date
            </label>
            <div
              style={{
                padding: '12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: '#fff',
              }}
            >
              <CardExpiryElement options={cardElementOptions} />
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <label
              style={{
                display: 'block',
                marginBottom: '5px',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#333',
              }}
            >
              CVC
            </label>
            <div
              style={{
                padding: '12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: '#fff',
              }}
            >
              <CardCvcElement options={cardElementOptions} />
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={!stripe || isProcessing}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: isProcessing ? '#ccc' : '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s',
          }}
        >
          {isProcessing ? 'Processing...' : 'Pay Now'}
        </button>
      </form>

      <div
        style={{
          marginTop: '15px',
          fontSize: '12px',
          color: '#666',
          textAlign: 'center',
        }}
      >
        Your payment is secured by Stripe
      </div>
    </div>
  )
}